import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { getOpenWbsItemDetailSpec } from '../../../../../../../../lib/functions/wbsItem'
import { ICellRendererParams, IRowNode } from 'ag-grid-community'
import { WbsItemIcon } from '../../../../../../../components/icons/WbsItemIcon'
import store from '../../../../../../../../store'
import { getPathByExternalId } from '../../../../../../../pages'
import { pushFunctionLayer } from '../../../../../../../../store/functionLayer'
import { FloatingEditableIcon } from '../../common/text'
import { inputErrorColor } from '../../../../lib/validator'
import { setError } from '../../../../../../BulkSheetView/lib/validation'
import { FunctionProperty } from '../../../../../../../../lib/commons/appFunction'
import validator from '../../../../../../meta/validator'

// 軽量スケルトンローダー（CSS onlyでパフォーマンス影響を最小化）
const skeletonStyle: React.CSSProperties = {
  height: 16,
  borderRadius: 4,
  background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.5s infinite',
}

// CSSアニメーションをheadに一度だけ注入
if (
  typeof document !== 'undefined' &&
  !document.getElementById('skeleton-style')
) {
  const style = document.createElement('style')
  style.id = 'skeleton-style'
  style.textContent = `
    @keyframes shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
  `
  document.head.appendChild(style)
}

// 軽量スケルトンコンポーネント（memo化）
const CellSkeleton = memo(() => (
  <div
    className="sevend-ag-cell-project-plan-tree"
    style={{ display: 'flex', alignItems: 'center', padding: '4px 0', gap: 8 }}
  >
    <div
      style={{
        ...skeletonStyle,
        width: 20,
        height: 20,
        flexShrink: 0,
        borderRadius: 4,
      }}
    />
    <div style={{ ...skeletonStyle, flex: 1, maxWidth: 120, height: 14 }} />
  </div>
))

interface Props extends ICellRendererParams {
  value: string
  onCloseWbsItemDetail?: (wbsItem) => void
  beforeOpenWbsItemDetail?: (uuid: string) => Promise<boolean | undefined>
  onOpenWbsItemDetail?: () => void
  uiMeta?: Partial<FunctionProperty>
}

export const ProjectPlanTreeCellRenderer = memo(
  (props: Props) => {
    const {
      value,
      data,
      uiMeta,
      onCloseWbsItemDetail,
      beforeOpenWbsItemDetail,
      onOpenWbsItemDetail,
      node,
    } = props
    const ref = useRef<HTMLDivElement | null>(null)
    const textRef = useRef<HTMLDivElement | null>(null)
    const isValid = useRef<boolean>(true)
    const [lineClamp, setLineClamp] = useState(1)

    useEffect(() => {
      const err = uiMeta
        ? validator
            .validate(value, data, uiMeta as FunctionProperty, () => undefined)
            ?.getMessage()
        : undefined
      setError(data, err, props)
      isValid.current = !err
    }, [value])

    const openDetail = useCallback(
      async (node: IRowNode, event: any) => {
        const d = node.data
        const w = d?.body?.wbsItem ?? d?.wbsItem
        if (!w) return
        if (d.added || d.edited) {
          const success = await beforeOpenWbsItemDetail?.(d.uuid)
          if (!success) return
        }
        onOpenWbsItemDetail && onOpenWbsItemDetail()
        const openDetailSpec = await getOpenWbsItemDetailSpec(true, w)
        const layer = openDetailSpec.layer
        openDetailSpec.onOpen && openDetailSpec.onOpen()
        if (
          !openDetailSpec.openInDialog ||
          (event && (event['ctrlKey'] || event['metaKey']))
        ) {
          window.open(`${getPathByExternalId(layer.externalId)}/${layer.code!}`)
        } else {
          store.dispatch(
            pushFunctionLayer({
              ...layer,
              onClose: async () => {
                onCloseWbsItemDetail?.(w)
              },
            })
          )
        }
      },
      [beforeOpenWbsItemDetail]
    )

    useEffect(() => {
      // requestAnimationFrameでDOM計測を遅延させ、Forced Reflowを回避
      const frameId = requestAnimationFrame(() => {
        const textElems = textRef.current
        if (!textElems) return
        const comStyles = window.getComputedStyle(textElems)
        const lineHeight = parseInt(
          comStyles.getPropertyValue('line-height').replace(/px/g, '')
        )
        const lineNo = Math.trunc(textElems.clientHeight / lineHeight)
        setLineClamp(lineNo > 0 ? lineNo : 1)
      })
      return () => cancelAnimationFrame(frameId)
    }, [])

    const wbsItem = data.body?.wbsItem ?? data.wbsItem
    const type = wbsItem?.wbsItemType

    // データ未取得時はスケルトンを表示
    if (!type) return <CellSkeleton />
    const description = wbsItem.description

    return (
      <div
        id={`wbs-tree-column-${props['rowIndex']}`}
        className="sevend-ag-cell-project-plan-tree"
        ref={ref}
      >
        <div className="sevend-ag-cell-project-plan-tree__icon-field">
          <button
            className="sevend-ag-cell-project-plan-tree__icon-button"
            onClick={e => openDetail(node, e)}
          >
            <WbsItemIcon type={type} />
          </button>
          {description && <NotificationDot />}
        </div>
        <div
          ref={textRef}
          className="sevend-ag-cell-project-plan-tree__text-field"
          style={{
            backgroundColor:
              value && isValid.current ? 'none' : inputErrorColor,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              overflow: 'hidden',
              display: '-webkit-box',
              textOverflow: 'ellipsis',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: lineClamp,
            }}
          >
            {value}
          </div>
        </div>
        <FloatingEditableIcon {...props} />
      </div>
    )
  },
  // カスタム比較関数: 描画に影響するpropsのみを比較
  (prevProps, nextProps) => {
    // valueが変わったら再レンダリング
    if (prevProps.value !== nextProps.value) return false

    // wbsItemの参照を取得
    const prevWbsItem = prevProps.data?.body?.wbsItem ?? prevProps.data?.wbsItem
    const nextWbsItem = nextProps.data?.body?.wbsItem ?? nextProps.data?.wbsItem

    // wbsItemが両方nullish
    if (!prevWbsItem && !nextWbsItem) return true
    // 片方だけnullish
    if (!prevWbsItem || !nextWbsItem) return false

    // wbsItemの描画に関係するプロパティを比較
    if (prevWbsItem.wbsItemType !== nextWbsItem.wbsItemType) return false
    if (prevWbsItem.description !== nextWbsItem.description) return false

    // rowIndexが変わったら再レンダリング（ID用）
    if (prevProps.rowIndex !== nextProps.rowIndex) return false

    return true
  }
)

const NotificationDot = () => (
  <p className="sevend-ag-cell-project-plan-tree__annotation-has-data" />
)
